export type Product = {
    id: number;
    barcode: string;
    code: string;
    main_code: string;
    status: string;
    name: string;
    size: string;
    pack: string;
    weight: number;
    active: boolean;
    default_expiry: number | null;
};

export type InboundDelivery = {
    id: number;
    identifier: string;
    reference: string;
    vrn: string;
    temp: number;
    started_at: Date;
    arrived_at: Date;
    completed_at: Date;
    attachments: Attachment[];
    pallets: Pallet[];
    user?: User;
};

export type Pallet = {
    id: number;
    identifier: string;
    items: InboundDeliveryItem[];
}

export type Attachment = {
    id: number;
    key: string;
    status: string;
    mime_type: string;
    filename: string;
    size: number;
    url?: string;
};

export type InboundDeliveryItem = {
    id: number;
    product: Product;
    shelf_life: number;
    quantity: number;
    weight: number;
    expires_at?: Date;
};

export type User = {
    id: number;
    name: string;
    email: string;
    /** Feature keys for this user; may be omitted on list endpoints. */
    functions: string[];
};

export type AuthUser = {
    id: number;
    name: string;
    functions: string[];
};

/** Keys returned by `/user` for feature access (navigation, etc.). */
export const APP_FUNCTION_KEYS = [
    "users",
    "goods_in",
    "assign_stock",
    "stock",
    "stock_locations",
    "non_conformance",
    "returns",
] as const;

export type AppFunctionKey = (typeof APP_FUNCTION_KEYS)[number];

const APP_FUNCTION_KEY_LABELS: Record<AppFunctionKey, string> = {
    users: "Users",
    goods_in: "Goods In",
    assign_stock: "Assign stock",
    stock: "Stock",
    stock_locations: "Stock locations",
    non_conformance: "Non-conformance",
    returns: "Returns",
};

export function formatAppFunctionKeyLabel(key: AppFunctionKey): string {
    return APP_FUNCTION_KEY_LABELS[key] ?? key;
}

export function userHasAppFunction(
    user: AuthUser | null | undefined,
    key: AppFunctionKey | null
): boolean {
    if (key === null) return true;
    return Boolean(user?.functions?.includes(key));
}

export type AuthResponse = {
    token: string;
};

export type StockLocation = {
    id: number;
    warehouse: Warehouse;
    name: string;
    levels: number;
    columns: number;
    uuid: string;
    major: number;
    minor: number;
};

export type Warehouse = {
    id: number;
    name: string;
};

export type ProductStock = {
    id: number;
    product: Product;
    location: StockLocation;
    level: number;
    column: number;
    quantity: number;
    weight: number;
    expires_at?: Date;
    shelf_life?: number;
    pallet_id?: number;
}

export type StockRelocationRequest = {
    id: number;
    product_stock: ProductStock;
    location: StockLocation | null;
    level: number | null;
    column: number | null;
    user: User;
    status: string;
    created_at: Date;
    split_pallet: boolean;
}

export type AuditLogEntry = {
    id: number;
    user: User;
    event: string;
    created_at: Date;
    type: string;
    display_type: string;
    old_values?: Record<string, any>;
    new_values?: Record<string, any>;
    description?: string;
    link?: AuditLogEntryLink;
}

export type AuditLogEntryLink = {
    type: string;
    id: number;
}

export type NonConformanceReason = "damaged" | "missing" | "incorrect" | "other";

const NON_CONFORMANCE_REASON_LABELS: Record<NonConformanceReason, string> = {
    damaged: "Damaged",
    missing: "Missing",
    incorrect: "Incorrect",
    other: "Other",
};

export function formatNonConformanceReason(reason: NonConformanceReason): string {
    return NON_CONFORMANCE_REASON_LABELS[reason] ?? reason;
}

/** Minimal product shape for non-conformance line items. */
export type NonConformanceProductRef = {
    id: number;
    name: string;
    code?: string;
};

export type NonConformanceReportListItem = {
    id: number;
    date: Date;
    supplier: string;
    reference: string;
    user: User;
};

export type NonConformanceReportItem = {
    id: number;
    product: NonConformanceProductRef;
    reason: NonConformanceReason;
    notes: string;
    attachments: Attachment[];
};

export type NonConformanceReport = {
    id: number;
    date: Date;
    supplier: string;
    reference: string;
    user: User;
    notes: string | null;
    items: NonConformanceReportItem[];
};

export type StockExpiryWarning = {
    stock: ProductStock;
    warning_level: number;
    expires_at: Date;
    expired: boolean;
}

export type Supplier = {
    id: number;
    name: string;
    code: string;
}

export type ExpectedDeliveryLine = {
    product: Product;
    qty: number;
    weight: number | null;
    line_no: number;
    description: string;
    pack_qty: number;
    label_qty: number;
    pallet_qty: number;
    units: string;
    supplier_code: string;
}

export type ExpectedDelivery = {
    id: number;
    supplier: Supplier;
    completed_at: Date | null;
    placed_at: Date | null;
    amended_at: Date | null;
    delivery_date: Date | null;
    po_number: string;
    pallet_qty: number;
    order_qty: number;
    email: string;
    lines: ExpectedDeliveryLine[];
}

export type SoftwareDownload = {
    version: number;
    build: number;
    url: string;
}